import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { icons } from '../../../assets/icons';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getDeliveryById } from '../../../api/redux/slices/Admin';
import { showToast } from '../../../components/modal';
import { convertDate, roundToThreeDecimals } from '../../../helpers/modules';
import { LoaderComponent } from '../../../helpers/components/loader';
import ImprimerLogo from '../../../assets/icons/svg/document-text.svg';
import { ClientInfo, ShopInfo } from '../../../components/userInfo';
import { useReactToPrint } from 'react-to-print';
import PrintToPdf from './PrintToPdf';

const DeliveryDetail = () => {
	const { deliveryDetails } = useSelector((state) => state.admin);
	const dispatch = useDispatch();
	const params = useParams();
	const navigate = useNavigate();
	const token = localStorage.getItem('accesstoken');

	const [loadingData, setLoadingData] = useState(false);
	const [countryMap, setCountryMap] = useState({});
	const [countryNames, setCountryNames] = useState({});
	const componentRef = useRef();
	const handlePrint = useReactToPrint({
		content: () => componentRef.current,
	});

	// Using the SAME logic as PrintToPdf component (working)
	useEffect(() => {
		fetch('https://cdn.jsdelivr.net/npm/world-countries@4/dist/countries.json')
			.then(res => res.json())
			.then(data => {
				const map = {};
				const names = {};
				data.forEach(c => {
					if (c.cca3 && c.cca2) {
						map[c.cca3] = c.cca2.toLowerCase();
						names[c.cca3] = c.name.common;
					}
				});
				setCountryMap(map);
				setCountryNames(names);
			})
			.catch(err => console.error('Failed to fetch country codes:', err));
	}, []);

	const getFlagUrl = (cca3) => {
		if (!cca3 || !countryMap[cca3]) return null;
		return `https://hatscripts.github.io/circle-flags/flags/${countryMap[cca3]}.svg`;
	};

	const getCountryName = (cca3) => {
		return countryNames[cca3] || cca3;
	};

	const stableDispatchOrderById = useCallback(() => {
		setLoadingData(true);
		dispatch(getDeliveryById({ token: token, id: params.id }))
			.unwrap()
			.catch((err) => {
				showToast(err?.message || 'can not fetch this delivery', 'error');
				setTimeout(() => { navigate(`/admin/deliveries/${params.page}`); }, 1000);
			})
			.finally(() => setLoadingData(false));
	}, [dispatch, params, token, navigate]);

	useEffect(() => {
		if (token && params.id) stableDispatchOrderById();
	}, [stableDispatchOrderById, token, params]);

	const hasCompany = () => deliveryDetails?.clientCompany ? true : false;

	const isTunisia = !deliveryDetails?.delivery?.order_id?.shopInfo?.country || deliveryDetails?.delivery?.order_id?.shopInfo?.country === 'TUN';
	const shopCountry = deliveryDetails?.delivery?.order_id?.shopInfo?.country;

	const calculateTotals = () => {
		let totalHT = 0;
		let totalTVA = 0;
		const tvaGroups = {};

		deliveryDetails?.list?.forEach((item) => {
			const price = item?.price || 0;
			const quantity = item?.quantity || 0;
			const tvaRate = item?.tva || 0;
			const montantHT = price * quantity;
			const tvaAmount = montantHT * (tvaRate / 100);
			totalHT += montantHT;
			totalTVA += tvaAmount;
			if (!tvaGroups[tvaRate]) tvaGroups[tvaRate] = 0;
			tvaGroups[tvaRate] += tvaAmount;
		});

		const timbre = isTunisia ? (deliveryDetails?.totals?.timbre || 1) : 0;
		const totalTTC = totalHT + totalTVA + timbre;
		return { totalHT, totalTVA, totalTTC, timbre, tvaGroups };
	};

	const totals = calculateTotals();

	const calculatePlatformFee = () => {
		if (!hasCompany()) return 0;
		return totals.totalTTC * 0.1;
	};

	const platformFee = calculatePlatformFee();

	if (loadingData) return <LoaderComponent />;

	return (
		<div id='deliveryDetails'>
			<div className='details-delivery-header'>
				<Link to={`/admin/deliveries/${params.page}`}>
					<icons.BsArrowLeft style={{ color: '#5B5B5B', marginRight: '10px' }} />
					<h1>Bons de livraisons</h1>
				</Link>
				<div style={{ display: 'flex', gap: '10px' }} className='add-btns'>
					<button onClick={handlePrint} style={{ display: 'flex', gap: '10px' }} className='button-primary'>
						<img src={ImprimerLogo} alt='imprimer logo' />Imprimer
					</button>
				</div>
			</div>
			<div id='title-delivery'><h1>🛒 JEUN'ESS MARKET</h1></div>
			<div className='container'>
				{!isTunisia && (
					<div className='platform-fee-alert' style={{ backgroundColor: '#E0F2FE', border: '2px solid #0284C7', borderRadius: '8px', padding: '15px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
						<div style={{ backgroundColor: '#0284C7', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
							<icons.BsGlobe style={{ color: 'white', fontSize: '20px' }} />
						</div>
						<div>
							<h3 style={{ margin: '0 0 5px 0', color: '#075985', fontSize: '16px', fontWeight: 'bold' }}>🌍 Livraison Internationale</h3>
							<p style={{ margin: '0', color: '#075985', fontSize: '14px', lineHeight: '1.5' }}>Cette livraison est pour un shop hors Tunisie. TVA et timbre ne sont pas applicables.</p>
						</div>
					</div>
				)}

				<div className='platform-fee-alert' style={{ backgroundColor: hasCompany() ? '#FFF3CD' : '#E8F5E9', border: hasCompany() ? '2px solid #FFA000' : '2px solid #4CAF50', borderRadius: '8px', padding: '15px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
					<div style={{ backgroundColor: hasCompany() ? '#FFA000' : '#4CAF50', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
						{hasCompany() ? <icons.BsExclamationTriangle style={{ color: 'white', fontSize: '20px' }} /> : <icons.BsCheckCircle style={{ color: 'white', fontSize: '20px' }} />}
					</div>
					<div>
						<h3 style={{ margin: '0 0 5px 0', color: hasCompany() ? '#856404' : '#2E7D32', fontSize: '16px', fontWeight: 'bold' }}>{hasCompany() ? '📢 Frais de Plateforme Marketing' : '✅ Frais de Plateforme Marketing'}</h3>
						{hasCompany() ? (
							<p style={{ margin: '0', color: '#856404', fontSize: '14px', lineHeight: '1.5' }}>
								<strong>10% du montant total</strong> de cette livraison sera prélevé pour la plateforme. <br />
								Montant total : <strong>{roundToThreeDecimals(totals.totalTTC)} DT</strong> → Frais (10%) : <strong style={{ color: '#D32F2F', fontSize: '15px' }}>{roundToThreeDecimals(platformFee)} DT</strong>
								<br /><span style={{ fontSize: '12px', fontStyle: 'italic' }}>💡 Ces frais peuvent être récupérés sous forme de biens ou d'achats auprès de la plateforme.</span>
							</p>
						) : (
							<p style={{ margin: '0', color: '#2E7D32', fontSize: '14px', lineHeight: '1.5' }}>Aucun frais de marketing n'est appliqué. Frais de plateforme : <strong>0 DT</strong></p>
						)}
					</div>
				</div>

				<div className='credentials'>
					<div className='left'>
						<div className='left-el'><label>Numéro</label><span>{deliveryDetails?.delivery?.reference || '-'}</span></div>
						<div className='left-el'><label>Date</label><span>{deliveryDetails?.delivery?.createdAt ? convertDate(deliveryDetails.delivery.createdAt, 'DD-MM-YYYY') : '-'}</span></div>
						<div className='left-el'><label>Référence code client</label><span>{deliveryDetails?.delivery?.order_id?.clientInfo?.customs_code || '-'}</span></div>
					</div>
					<div className='left'>
						<div className='left-el'>
							<ClientInfo clientInfo={deliveryDetails?.delivery?.order_id?.clientInfo} />
							<ShopInfo shop={deliveryDetails?.delivery?.order_id?.shopInfo} />
							{shopCountry && (
								<div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
									{getFlagUrl(shopCountry) && (
										<img 
											src={getFlagUrl(shopCountry)} 
											alt={getCountryName(shopCountry)} 
											width="20" 
											height="20" 
											style={{ borderRadius: '50%' }} 
										/>
									)}
									<span style={{ fontSize: '0.85rem', color: '#35435f', fontWeight: 600 }}>
										{getCountryName(shopCountry)}
									</span>
								</div>
							)}
						</div>
					</div>
				</div>
				<div className='devis'>
					<div className='devis-head'><h1>BON DE LIVRAISON</h1><span /></div>
					<div className='devis-table'>
						<table className='offres'>
							<thead>
								<tr id='titres'>
									<th>RÉFÉRENCE</th>
									<th>CODE E.A.N</th>
									<th>DÉSIGNATION</th>
									<th>QTÉ</th>
									<th>VOLUME</th>
									<th>PRIX UNIT H.T</th>
									<th>TVA</th>
									<th>MONTANT H.T</th>
								</tr>
							</thead>
							<tbody>
								{deliveryDetails?.list?.map((item, index) => (
									<tr className='elements' key={index}>
										<td>{item?.reference}</td>
										<td>{item?.refranceCTL}</td>
										<td>{item?.designation}</td>
										<td>{item?.quantity}</td>
										<td>{item?.volume != null ? roundToThreeDecimals(item.volume) : '-'}</td>
										<td>{roundToThreeDecimals(item?.price || 0)} DT</td>
										<td>{item?.tva || 0}%</td>
										<td>{roundToThreeDecimals((item?.price || 0) * (item?.quantity || 0))} DT</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>

				<div className='totals-section'>
					<div className='totals-box'>
						<div className='total-line'><span>TOTAL H.T : </span><span>{roundToThreeDecimals(totals.totalHT)} DT</span></div>
						{Object.entries(totals.tvaGroups).map(([rate, amount]) => (
							<div className='total-line' key={rate}><span>TVA ({rate}%) :</span><span>{roundToThreeDecimals(amount)} DT</span></div>
						))}
						{isTunisia && (
							<div className='total-line'><span>TIMBRE : </span><span>{roundToThreeDecimals(totals.timbre)} DT</span></div>
						)}
						<div className='total-line grand-total'><span>TOTAL TTC : </span><span>{roundToThreeDecimals(totals.totalTTC)} DT</span></div>
						<div className='total-line' style={{ borderTop: '2px solid #FFA000', paddingTop: '10px', marginTop: '10px' }}>
							<span style={{ color: hasCompany() ? '#D32F2F' : '#4CAF50', fontWeight: 'bold' }}>FRAIS MARKETING (10%) :</span>
							<span style={{ color: hasCompany() ? '#D32F2F' : '#4CAF50', fontWeight: 'bold' }}>{roundToThreeDecimals(platformFee)} DT</span>
						</div>
					</div>
				</div>

				<div className='footer'>
					<div className='left'>
						<ul>
							<li>Nom de société : <strong>{deliveryDetails?.companyInfo?.name || '-'}</strong></li>
							<li>Addresse : {deliveryDetails?.companyInfo?.address || '-'}</li>
							<li>CODE TVA : <span>{deliveryDetails?.companyInfo?.tva_code || '-'}</span></li>
							<li>RC : <span>{deliveryDetails?.companyInfo?.r_c || '-'}</span></li>
							<li>Code en Douane : <span>{deliveryDetails?.companyInfo?.customs_code || '-'}</span></li>
						</ul>
					</div>
					<div className='left'>
						<ul>
							<li>Tél : <span>{deliveryDetails?.companyInfo?.phone || '-'}</span></li>
							<li>Fax : <span>{deliveryDetails?.companyInfo?.fax || '-'}</span></li>
							<li>Email : <span>{deliveryDetails?.companyInfo?.email || '-'}</span></li>
						</ul>
					</div>
				</div>
			</div>
			<div style={{ display: 'none' }}>
				<PrintToPdf componentRef={componentRef} adminCompany={deliveryDetails?.adminCompany} deliveryDetails={deliveryDetails} />
			</div>
		</div>
	);
};

export default DeliveryDetail;